import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@reactuses/core';
import { motion, useAnimationControls } from 'framer-motion';
import { getQuizzQuestions } from './_utils/getQuizzQuestions';
import { Question } from './_utils/types';
import invalidIcon from '@/assets/icons/invalid.svg?url';
import validIcon from '@/assets/icons/valid.svg?url';
import { GameHeader } from '@/components/GameHeader';
import {
  DefaultPopup,
  DefaultPopupProps,
} from '@/components/popups/DefaultPopup';
import { StartGamePopup } from '@/components/popups/StartGamePopup';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SvgIcon } from '@/components/ui/SvgIcon';
import Title from '@/components/ui/title';
import { assets } from '@/lib/assets';
import { useBrowserLeaveActions } from '@/lib/hooks/useBrowserLeaveActions';
import { cn } from '@/lib/utils';
import { getRandomSuccessTitleKey } from '@/lib/utils/getRandomSuccessTitleKey';
import {
  muteSounds,
  playSound,
  stopSound,
  unmuteSounds,
} from '@/lib/utils/playSound';
import { useTimer } from '@/lib/utils/useTimer';
import { waitForSeconds } from '@/lib/utils/waitForSeconds';
import { gameDataContext, GameOrder } from '@/providers/GameDataProvider';

const QUESTIONS_PER_GAME = 6;

export const PageQuizzGame = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    increaseScore,
    nextGamePath,
    openProgressPopup,
    currentGameIndex,
    setCurrentGameScore,
  } = useContext(gameDataContext);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState<string>();
  const [isChecking, setIsChecking] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [isQuestionsLoaded, setIsQuestionsLoaded] = useState(false);

  const quizzIndex = window.location.pathname.includes('quizz_01') ? 0 : 1;
  const quizzDisplayName = quizzIndex === 0 ? 'game_quizz_1' : 'game_quizz_2';

  const [infoPopupData, setInfoPopupData] = useState<DefaultPopupProps>({
    title: '',
    description: '',
    buttonLabel: '',
    onButtonClick: () => {
      console.log('continue function not set');
    },
  });
  const {
    isOpen: infoPopup_open,
    onClose: close_infoPopup,
    onOpen: open_infoPopup,
  } = useDisclosure();

  const {
    isOpen: startPopupOpen,
    onClose: close_startPopup,
    onOpen: open_startPopup,
  } = useDisclosure();

  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  // on browser leave, we do not reload as we want to keep the score
  // But we mute and unmute sounds
  useBrowserLeaveActions({
    reloadOnReturn: false,
    onReturnCallback: () => {
      unmuteSounds();
    },
    onLeaveCallback: () => {
      muteSounds();
    },
  });

  // Animation controls for question container
  const questionContainerControls = useAnimationControls();

  // Load questions dynamically based on current language
  const loadQuestions = async () => {
    setIsQuestionsLoaded(false);
    const questions = await getQuizzQuestions({
      quizzIndex,
      numQuestions: QUESTIONS_PER_GAME,
    });
    setQuestionList(questions);
    setIsQuestionsLoaded(true);
  };

  // Effect to load questions when language changes
  useEffect(() => {
    loadQuestions();

    // Add language change listener
    const handleLanguageChanged = () => {
      loadQuestions();
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n.language]);

  // CURRENT QUESTION
  const question = useMemo(
    () => questionList[questionIndex],
    [questionIndex, questionList]
  );

  // TODO: remove chars logic here
  // const currentChar = assets.chars.bike;
  // question?.item
  //   ? assets.chars[question.item as keyof typeof assets.chars]
  //   : undefined;

  const correctAnswer = question ? `radio-${question.correctAnswer[0]}` : ''; // Define correctAnswer based on current question

  const [gameStarted, setGameStarted] = useState(false);
  const {
    start: startTimer,
    timeInSec,
    stop: stopTimer,
    reset: resetTimer,
  } = useTimer();

  // --------------------- methods ------------------------

  const startGame = () => {
    // Only allow starting the game when questions are loaded
    if (!isQuestionsLoaded) {
      console.warn('Questions not loaded yet, please wait...');
      return;
    }
    resetTimer();
    startTimer();
    setGameStarted(true);
    setCurrentGameScore(0);
    close_startPopup();
    setScore(0);
  };

  const retry = () => {
    // reset score
    setScore(0);
    setCurrentGameScore(0);
    // reset questions
    setQuestionIndex(0);
    setAnswer(undefined);
    setShowCorrectAnswer(false);
    setIsChecking(false);
    // load new questions
    loadQuestions();

    // reset game
    setGameOver(false);
    close_infoPopup();
    open_startPopup();
  };

  const showInfoPopup = (
    title: string,
    description: string,
    buttonLabel: string,
    buttonCallback: () => void,
    links?: string[]
  ) => {
    setInfoPopupData({
      title,
      description,
      buttonLabel,
      onButtonClick: buttonCallback,
      links: links || [],
    });
    open_infoPopup();
  };

  // display popup failed, on close, we display the wrong answer for 1second and then display the correct answer popup
  const showFailPopup = () => {
    // auto remove popup
    showInfoPopup(
      t('popup.quizz.fail', { VALUE: question.correctAnswer[0] + 1 }),
      '',
      '',
      () => {
        // this will never be calld as we didn't use a button label.
        console.log('close popup');
      }
    );

    setTimeout(() => {
      close_infoPopup();
      setShowCorrectAnswer(true);
      setTimeout(() => {
        showFailExplanationPopup();
      }, 1000);
    }, 3000);
  };

  const showFailExplanationPopup = () => {
    showInfoPopup(
      '',
      question.explain,
      t('popup.info.continue'),
      () => {
        close_infoPopup();
        setTimeout(() => {
          onContinueToNextQuestion();
        }, 1000);
      },
      question.links // for explanation popup, we pass the links
    );
  };

  const showSuccessPopup = () => {
    showInfoPopup(
      t(getRandomSuccessTitleKey()),
      question.explain,
      t('popup.info.continue'),
      () => {
        close_infoPopup();
        setTimeout(() => {
          onContinueToNextQuestion();
        }, 1000);
      },
      question.links // for explanation popup, we pass the links
    );
  };

  const checkResult = () => {
    const isScored = answer === correctAnswer; // correct answer
    setIsChecking(true);

    // add diamond if scored
    if (isScored) {
      setScore(score + 1);
      setShowCorrectAnswer(true);
      increaseScore(1);
      // addCharacterToInventory(question.item);
      playSound(assets.game_quizz.sounds.success);
    } else {
      playSound(assets.game_quizz.sounds.wrong);
    }
    // wait then show success or fail popup
    setTimeout(() => {
      if (isScored) {
        showSuccessPopup();
      } else {
        showFailPopup();
      }
    }, 1000);
  };

  const onContinueToNextQuestion = () => {
    // transition!
    // Animate out the current question
    questionContainerControls
      .start({
        opacity: 0,
        y: 50,
        transition: { duration: 0.3 },
      })
      .then(() => {
        if (questionIndex < questionList.length - 1) {
          setQuestionIndex(questionIndex + 1);
          setAnswer(undefined);

          setIsChecking(false);
          setShowCorrectAnswer(false);
          close_infoPopup();

          // Animate in the new question
          questionContainerControls.start({
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: 'easeOut' },
          });
        } else {
          setGameOver(true);
        }
      });
  };

  const endGame = async () => {
    stopTimer();

    await waitForSeconds(0.5);

    // Case GAME FAILED
    if (score === 0) {
      showInfoPopup(
        t('popup.fail.title'),
        t('popup.fail.description'),
        t('common.retry'),
        () => {
          retry();
        }
      );
    }
    // Case GAME  SUCCESS
    else {
      // show end game popup
      showInfoPopup(
        t('popup.success.title', { score: score }),
        // t(getRandomSuccessTitleKey()),
        currentGameIndex === GameOrder.length - 1
          ? t('popup.success.description.final')
          : t('popup.success.description', { score: score }),
        t('popup.success.button'),
        () => {
          navigate(nextGamePath);
        }
      );
    }
  };

  // --------------------- effects ------------------------

  useEffect(() => {
    if (gameOver) {
      endGame();
    }
  }, [gameOver]);

  // // Animate when a new question appears
  useEffect(() => {
    if (gameStarted && question) {
      questionContainerControls.start({
        opacity: [0, 1],
        y: [20, 0],
        transition: { duration: 0.5 },
      });
    }
  }, [gameStarted, questionIndex, question]);

  useEffect(() => {
    setTimeout(() => {
      openProgressPopup(() => {
        open_startPopup();
      });
    }, 1000);

    playSound(assets.game_quizz.sounds.music, {
      loop: true,
      volume: 0.5,
    });

    return () => {
      stopSound(assets.game_quizz.sounds.music, 1000, true);
    };
  }, [navigate]);

  // --------------------- render ------------------------

  return (
    <>
      {/* // --------------------- Header ------------------------ */}
      <GameHeader open timeInSec={timeInSec} showTimer={false} />

      {/* // --------------------- game ------------------------ */}
      <div className="fixed left-0 top-0 flex h-screen w-screen justify-center bg-black">
        <img
          alt="game background"
          src={assets.game_quizz.bg}
          className={cn(
            'absolute h-full w-auto object-cover opacity-70 blur-sm filter',
            gameStarted && 'opacity-40'
          )}
        />
      </div>

      {gameStarted && (
        <div className="relative flex-col pt-10">
          {/* // --------------------- Question item ------------------------ */}
          <motion.div
            className="relative w-full flex-col space-y-3 overflow-hidden px-6 py-20 md:px-20"
            animate={questionContainerControls}
            initial={{ opacity: 1, y: 0 }}
          >
            <div className="relative flex min-h-20 w-full items-center justify-center md:min-h-24">
              <Title
                i18nKey={t(question.title)}
                className={cn(
                  'py-0 text-center text-2xl md:py-8 md:text-4xl'
                  // isChecking && answer !== correctAnswer && 'opacity-0'
                )}
              />
              {/* {
                // display message in case of wrong answer, absolute so it doesn't "move" the layout as the title is hidden
                isChecking && answer !== correctAnswer && (
                  <motion.div
                    className="absolute bottom-0 left-0 w-full text-center text-2xl md:text-4xl"
                    initial={{ scale: 0.2, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'backOut' }}
                  >
                    <Title
                      i18nKey={t('game_quizz.check.title')}
                      className={cn('w-full text-center text-3xl md:text-4xl')}
                    />
                  </motion.div>
                )
              } */}
            </div>

            <RadioGroup value={answer}>
              <div className="gap w-full flex-col space-y-4">
                {question.options.map((option, index) => {
                  const radioID = `radio-${index}`;
                  const isSelected = radioID === answer;
                  const isCorrect =
                    showCorrectAnswer && radioID === correctAnswer;
                  const isWrong =
                    isChecking && isSelected && radioID !== correctAnswer;

                  return (
                    <div
                      key={option}
                      className={cn(
                        'flex min-h-20 cursor-pointer items-center justify-between rounded-2xl border-4 border-primary bg-primary p-2 px-6 text-lg transition-all duration-150 ease-in-out hover:scale-105',
                        answer === radioID ? 'border-secondary' : 'bg-primary',
                        isChecking && 'pointer-events-none',
                        // if checking, we "return" cards that is correct or selected
                        (isWrong || isCorrect) &&
                          'pointer-events-none -scale-y-100 duration-200',
                        // show correct answer in green
                        isCorrect &&
                          'border-2 border-green-400 bg-green-500 bg-opacity-50',
                        // if user answer is not correct answer we display it red
                        isWrong &&
                          'border-2 border-red-400 bg-red-500 bg-opacity-50'
                      )}
                      onClick={() => setAnswer(radioID)}
                    >
                      <label
                        className={cn(
                          'pointer-events-none w-full select-none pr-6',
                          isWrong || isCorrect ? '-scale-y-100' : ''
                        )}
                      >
                        {option}
                      </label>
                      <div className="relative">
                        {!isWrong && !isCorrect && (
                          <RadioGroupItem
                            id={radioID}
                            value={radioID}
                            className={cn(
                              'h-6 w-6 border border-gray-400 bg-primary text-primary',
                              isSelected &&
                                'border-secondary bg-secondary text-secondary',
                              isChecking &&
                                isCorrect &&
                                'border-green-500 bg-green-500 text-green-500',
                              isChecking &&
                                isSelected &&
                                !isCorrect &&
                                'border-red-500 bg-red-500 text-red-500'
                            )}
                          />
                        )}

                        {isWrong && (
                          <SvgIcon
                            src={invalidIcon}
                            alt="invalid"
                            size={6}
                            className="-scale-y-100"
                          />
                        )}
                        {isCorrect && (
                          <SvgIcon
                            src={validIcon}
                            alt="valid"
                            size={6}
                            className="-scale-y-100"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </RadioGroup>

            {answer && !isChecking && (
              <motion.div
                className="flex grow justify-center"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, easings: 'easeInOut' }}
              >
                <Button
                  variant="secondary"
                  className="mt-10 w-60 rounded-3xl py-8 text-2xl"
                  onClick={() => {
                    checkResult();
                  }}
                >
                  {t('game_quizz.button.continue')}
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
      )}

      {/* // --------------------- popup ------------------------ */}

      {/* // --------------------- POPUPS ------------------------ */}
      <DefaultPopup
        isOpen={infoPopup_open}
        onClose={close_infoPopup}
        {...infoPopupData}
      />

      {/* // --------------------- start popup ------------------------ */}
      <StartGamePopup
        isOpen={startPopupOpen}
        onClose={startGame}
        title={t(`${quizzDisplayName}.title`)}
        description={t(`${quizzDisplayName}.description`)}
      />
    </>
  );
};
